/**
 * Outing Leaderboard Component
 * Live leaderboard display for golf outings.
 */
import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Target, RefreshCw, Wifi } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { useOutingApi } from '../hooks/useOutingApi';

export default function OutingLeaderboard({ outingId, refreshInterval = 30000 }) {
  const api = useOutingApi();
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchLeaderboard = async () => {
    try {
      const data = await api.getLeaderboard(outingId);
      if (data.success) {
        setLeaderboard(data);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    
    // Auto-refresh
    const interval = setInterval(fetchLeaderboard, refreshInterval);
    return () => clearInterval(interval);
  }, [outingId, refreshInterval]);

  if (loading && !leaderboard) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
      </div>
    );
  }

  const teams = leaderboard?.teams || [];
  const ctpLeaders = leaderboard?.ctp_leaders || {};
  const topThree = teams.slice(0, 3);
  const rest = teams.slice(3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white uppercase tracking-tight">
            {leaderboard?.outing_name || 'Leaderboard'}
          </h2>
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Wifi className="w-4 h-4 text-green-400" />
            <span>Live</span>
            {lastUpdate && (
              <span>• Updated {lastUpdate.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLeaderboard}
          className="border-white/20"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Podium - Top 3 */}
      {topThree.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* 2nd Place */}
          <div className="pt-8">
            {topThree[1] && (
              <PodiumCard 
                team={topThree[1]} 
                position={2}
                className="h-36"
              />
            )}
          </div>
          
          {/* 1st Place */}
          <div>
            {topThree[0] && (
              <PodiumCard 
                team={topThree[0]} 
                position={1}
                className="h-44"
              />
            )}
          </div>
          
          {/* 3rd Place */}
          <div className="pt-12">
            {topThree[2] && (
              <PodiumCard 
                team={topThree[2]} 
                position={3}
                className="h-32"
              />
            )}
          </div>
        </div>
      )}

      {/* Rest of Leaderboard */}
      {rest.length > 0 && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-0">
            {rest.map((team, index) => (
              <LeaderboardRow 
                key={team.team_id} 
                team={team} 
                index={index + 4}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {teams.length === 0 && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No scores yet. Check back once teams start playing!</p>
          </CardContent>
        </Card>
      )}

      {/* CTP Leaders */}
      {Object.keys(ctpLeaders).length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-yellow-400" />
            Closest to Pin Leaders
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(ctpLeaders).map(([hole, entry]) => (
              <Card key={hole} className="bg-[#121212] border-yellow-500/30">
                <CardContent className="p-4 text-center">
                  <Badge className="bg-yellow-500/20 text-yellow-400 mb-2">
                    Hole {hole}
                  </Badge>
                  <p className="font-semibold text-white">{entry.player_name}</p>
                  <p className="text-sm text-zinc-400">{entry.team_name}</p>
                  <p className="text-xl font-bold text-yellow-400 mt-2">
                    {entry.distance_display}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PodiumCard({ team, position, className }) {
  const positionConfig = {
    1: { bg: 'bg-gradient-to-b from-yellow-500/30 to-yellow-500/10', border: 'border-yellow-500', icon: '🥇' },
    2: { bg: 'bg-gradient-to-b from-zinc-400/30 to-zinc-400/10', border: 'border-zinc-400', icon: '🥈' },
    3: { bg: 'bg-gradient-to-b from-amber-700/30 to-amber-700/10', border: 'border-amber-700', icon: '🥉' }
  };
  
  const config = positionConfig[position];
  
  return (
    <Card className={`${config.bg} border ${config.border} ${className} flex flex-col justify-end`}>
      <CardContent className="p-4 text-center">
        <span className="text-3xl">{config.icon}</span>
        <p className="font-bold text-white mt-2 truncate">{team.team_name}</p>
        <p className="text-2xl font-black text-white">
          {team.relative_score === 0 ? 'E' : team.relative_score > 0 ? `+${team.relative_score}` : team.relative_score}
        </p>
        <p className="text-xs text-zinc-400">Thru {team.thru}</p>
      </CardContent>
    </Card>
  );
}

function LeaderboardRow({ team, index }) {
  return (
    <div className="flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
      <div className="flex items-center gap-4">
        <span className="w-8 text-center text-zinc-400 font-mono">{index}</span>
        <div>
          <p className="font-semibold text-white">{team.team_name}</p>
          <p className="text-xs text-zinc-400">Thru {team.thru}</p>
        </div>
      </div>
      <div className="text-right">
        <p className={`text-xl font-bold ${
          team.relative_score < 0 ? 'text-green-400' :
          team.relative_score > 0 ? 'text-red-400' : 'text-white'
        }`}>
          {team.relative_score === 0 ? 'E' : team.relative_score > 0 ? `+${team.relative_score}` : team.relative_score}
        </p>
        <p className="text-xs text-zinc-500">{team.score}</p>
      </div>
    </div>
  );
}
